import {
  IonPage,
  IonToolbar,
  IonButtons,
  IonContent,
  IonButton,
  IonFooter,
  IonLoading,
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonInput,
  IonSelectOption,
  IonNote,
  useIonAlert,
  IonToggle,
  IonTextarea,
  IonIcon,
  IonListHeader,
  IonFab,
  isPlatform,
  IonFabButton,
  IonFabList,
  IonModal,
  IonHeader,
  IonTitle,
  IonRadioGroup,
  IonRadio,
  useIonViewDidLeave,
  IonAccordion,
  IonAccordionGroup,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import {
  getUserHasAccess,
  getUserId,
  getCreateWorkOrderFromSrAccess,
} from "../util/UserHelper";
import StandardRequestModal from "../components/StandardRequestModal";
import "./CreateQuickWO.css";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  getTemplateForWindow,
  getTemplateForWorkOrder,
  getTasksList,
  getWorkOrder,
} from "../api/WorkOrders";
import { getParentShopAndPlanner } from "../api/EquipmentMasters";
import { getWorkOrderControl } from "../api/PlantSetups";
import { decodeParam, encodeParam } from "../util/ApiHelper";
import { convertBlobToBase64, readDataAsBase64 } from "../util/FileHelper";
import { pickImageFromGallery } from "../util/PhotoPicker";

import {
  barcodeOutline,
  barcodeSharp,
  camera,
  cloudUpload,
  push,
  trashOutline,
  chevronDownOutline,
  searchOutline,
  searchSharp,
} from "ionicons/icons";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { ApiResponse } from "../models/api/ApiResponse";
import { addFollowup } from "../api/Events";
import { findEmployee } from "../api/Employees";
import { getValueSearchDetails } from "../api/QueryDesigner";
import FilterModal from "../components/FilterModal";
import React from "react";
import ISSPopover from "../components/ISSPopover";
import {
  getCascadeDependencyMap,
  getDependentObjects,
} from "../util/GetColumnsWithGroupHelper";
import {
  getValidValueDisplayText,
  compareValidValuesByDisplayText,
} from "../util/DropdownDisplayModeHelper";
import {
  getCascadeRequiredValuesForPlant,
  getParentFieldMappings,
  getStoreAccountCascadeValues,
} from "../util/StoresAccountHelper";
import { CascadeValue } from "../models/analysiscodes/AnalysisCodes";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { useOfflineStore } from "../store/offline-mode";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetCreateWoMetaData from "../hooks/work-order/useGetCreateWoMetaData";
import useGetCustomColumns from "../hooks/work-order/useGetCustomColumns";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import useCreateWo from "../hooks/work-order/useCreateWo";
import useGetCascadeValues from "../hooks/validation/useGetCascadeValues";
import {
  applyCascadeRequired,
  isCascadeChildRequired,
  isFieldRequired,
  updateCascadeChildOptions,
} from "../util/MetadataHelper";
import { useBarcodeManager } from "../hooks/useBarcodeManager";
import useAttachFollowupWo from "../hooks/work-order/useAttachFollowupWo";

interface ValidValues {
  Code: string;
  Description: string;
}

const CUSTOM_FIELD_PREFIX = "__udv__";

const getCustomFieldKey = (field: any) =>
  `${CUSTOM_FIELD_PREFIX}${field.UdId || field.IdText}`;

const isCustomField = (field: any) =>
  field.IsCustomField === true ||
  field.TabWareType === "Entity__UDV" ||
  String(field.PropertyName || "").startsWith(CUSTOM_FIELD_PREFIX);

const CreateQuickWO: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { pathname } = useLocation();
  const { equipId, workOrderId, offlineWoId, sr } = useParams<{
    equipId: string;
    workOrderId?: string;
    offlineWoId?: string;
    sr?: string;
  }>(); // TODO: Set up possibility of other param names for other quick work order types
  const displayWoId = !!workOrderId ? decodeParam(workOrderId) : undefined;
  // US 820773: when present, this page is creating a WO from a Standard Request.
  const srNumber = sr ? decodeParam(sr) : undefined;
  // const [area, setArea] = useState<string>();
  // const [department, setDepartment] = useState<string>();
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const [showLoading, setShowLoading] = useState(false);
  const [metadata, setMetadata] = useState<any[]>([]);
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [showError, setShowError] = useState<any>({});
  const [metadataError, setMetadataError] = useState(false);
  const [templateError, setTemplateError] = useState(false);
  const [emergencyAccess, setEmergencyAccess] = useState<boolean>(true);
  const [isEmergency, setIsEmergency] = useState<boolean>(false);
  // US 872838: dedicated source of truth for the "Down" (downtime) flag toggle,
  // for the same anti-echo reason as isEmergency above.
  const [isDown, setIsDown] = useState<boolean>(false);

  const [attachedDocuments, setAttachedDocuments] = useState<any[]>([]);
  const [propertyName, setPropertyName] = useState<string>("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const popover = useRef<HTMLIonPopoverElement>(null);
  const [filterModalColumnName, setFilterModalColumnName] = useState("");
  const [filterModalModuleName, setFilterModalModuleName] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [assignedTo, setAssignedTo] = useState<string>("");
  const [nextReviewer, setNextReviewer] = useState<string>("");
  const [requestor, setRequestor] = useState<string>("");
  const [showNextReviewerModal, setShowNextReviewerModal] = useState(false);
  const [showAssignedToModal, setShowAssignedToModal] = useState(false);
  const [showRequestorModal, setShowRequestorModal] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState<string>("");
  const [selectedAssignTo, setSelectedAssignTo] = useState<string>("");
  const [selectedRequestor, setSelectedRequestor] = useState<string>("");
  const [filter, setFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  let [assignedToValidData, setAssignedToValidData] = useState<ValidValues[]>(
    []
  );
  let [reviewerValidData, setReviewerValidData] = useState<ValidValues[]>([]);
  let [requestorValidData, setRequestorValidData] = useState<ValidValues[]>([]);
  let [filteredReviewerList, setFilteredReviewerList] = useState<ValidValues[]>(
    []
  );
  let [filteredAssignToList, setFilteredAssignToList] = useState<ValidValues[]>(
    []
  );
  let [filteredRequestorList, setFilteredRequestorList] = useState<
    ValidValues[]
  >([]);
  const [technicians, setTechnicians] = useState<string[]>(Array(6).fill(""));
  const [showTechnicianModals, setShowTechnicianModals] = useState<boolean[]>(
    Array(6).fill(false)
  );
  const [selectedTechnicians, setSelectedTechnicians] = useState<string[]>(
    Array(6).fill("")
  );
  let [technicianValidData, setTechnicianValidData] = useState<ValidValues[]>(
    []
  );
  let [filteredTechnicianLists, setFilteredTechnicianLists] = useState<
    ValidValues[][]
  >(Array(6).fill([]));

  const [cascadeValues, setCascadeValues] = useState<{
    [key: string]: CascadeValue[];
  }>({});
  const [template, setTemplate] = useState();
  // US 820773: SR task/subtask list to copy onto the new WO (from getTasksList).
  const [srTaskSubtaskList, setSrTaskSubtaskList] = useState<any[]>([]);
  // US 820773: "Copy from Standard Request" field + pop-up state.
  const [showSrModal, setShowSrModal] = useState(false);
  const [hasSrAccess, setHasSrAccess] = useState(false);
  //Cascade Dependency Map
  const dependencyMap = useRef(new Map<string, string[]>());

  //offline store
  const isOffline = useOfflineStore((state) => state.isOffline);

  //work-order store
  const createWoMetaData = useWorkOrderStore(
    useShallow((state) => state.createWoMetaData)
  );
  const customFields = useWorkOrderStore(
    useShallow((state) => state.customFields)
  );
  const allCascadeValues = useWorkOrderStore(
    useShallow((state) => state.cascadeValues)
  );
  // US 906024 - the plant's cascade "required" rules, loaded in the same batch as the values.
  const allCascadeRequiredValues = useWorkOrderStore(
    useShallow((state) => state.cascadeRequiredValues)
  );

  //update data store
  const addWorkOrderToCreate = useWoOfflineUpdatesStore(
    (state) => state.addWorkOrderToCreate
  );
  const updateById = useWoOfflineUpdatesStore((state) => state.updateById);
  const deleteById = useWoOfflineUpdatesStore((state) => state.deleteById);

  const { getCreateWoMetaData } = useGetCreateWoMetaData();
  const { getCustomColumns } = useGetCustomColumns();
  const { createWo } = useCreateWo();
  const { getCascadedValues } = useGetCascadeValues();
  const { scanText, scanClick } = useBarcodeManager();
  const { attachFollowupWo } = useAttachFollowupWo();

  const displayEquipId =
    isOffline || offlineWoId ? values.Equipment : decodeParam(equipId);

  // Set a number of things that depend on location
  // Using If block to set up for other types of quick work orders later, such as serial
  let subtitle = "";
  let routeBack = `/Equipment/${encodeParam(displayEquipId)}`;
  let routeForward = "";
  let templateWindowName = "";
  if (pathname.includes("requester")) {
    subtitle = "Create Request"; // TODO: Get translation for this
    routeForward = "/requester/workorder";
    templateWindowName = "wr";
  } else {
    if (workOrderId && displayWoId) {
      routeBack = `/workorder/${encodeParam(displayWoId)}`;
    }
    subtitle =
      translations["lbl_btn_create_quick_wo"] || "Create Quick Work Order";
    routeForward = "/workorder";
    templateWindowName = "quick";
  }

  if (isOffline) routeBack = "/home";

  const setValue = useCallback((propertyName: string, newVal: any) => {
    setValues((pre) => ({ ...pre, [propertyName]: newVal }));
  }, []);

  // Find the cascade child field for a given parent field's metadata, mirroring
  // the matching used in the combo onChange handler.
  const findCascadeChild = (parentMeta: any, fieldList: any[]) =>
    fieldList.find(
      (m) =>
        m.PropertyName === parentMeta.CascadeChild ||
        getParentFieldMappings(m.CascadeParent).includes(
          parentMeta.PropertyName
        ) ||
        getParentFieldMappings(parentMeta.CascadeChild).includes(m.PropertyName)
    );

  // Re-filter a cascade parent's mapped child options (e.g. WO Type -> Priority,
  // US 835011) for the parent's new value, and return the reset map that clears
  // any now-invalid dependent selections.
  const applyCascadeToChildren = (parentMeta: any, newValue: any) => {
    const name = parentMeta.PropertyName ?? "";
    const child = findCascadeChild(parentMeta, metadata);
    if (child && cascadeValues[name]?.length) {
      updateCascadeChildOptions(child, cascadeValues[name], newValue, setMetadata);
    }
    let cascadedDataToReset: { [key: string]: string } = {};
    getDependentObjects(name, dependencyMap.current).forEach((c) => {
      cascadedDataToReset = { ...cascadedDataToReset, [c]: "" };
    });
    /* US 906024 - re-evaluate whether the code just picked makes the child field mandatory.
       Outside the value-mapping guard above on purpose: a code that maps no child values at all
       can still carry the flag, which is the originating case for this rule. */
    applyCascadeRequired(
      allCascadeRequiredValues,
      name,
      newValue,
      values[parentMeta.CascadeParent ?? ""],
      child,
      Object.keys(cascadedDataToReset),
      setMetadata
    );
    return cascadedDataToReset;
  };

  const getTemplate = useCallback(async () => {
    if (isOffline) {
      setTemplate(undefined);
      return; //would not be able to download the template as it needs EquipId upfront
    }
    // US 820773: when creating from a Standard Request, pull the template from
    // the SR itself (copyValue=true) so every field, visible and hidden, is
    // seeded from the SR — mirrors the Web SR copy flow.
    const response = srNumber
      ? await getTemplateForWorkOrder({
          equipment: decodeParam(equipId),
          woNumber: srNumber,
          woType: "Routine",
          copyValue: "true",
        })
      : displayWoId
      ? await getTemplateForWorkOrder({
          equipment: decodeParam(equipId),
          woNumber: displayWoId,
          woType: "Routine",
          copyValue: "true",
        })
      : await getTemplateForWindow({
          equipment: decodeParam(equipId),
          windowName: templateWindowName,
          woType: "Routine",
        });

    if (response.status !== 200) {
      setTemplateError(true);
      setTemplate(undefined);
      return;
    }

    // US 869961 / Bug 873785: When the hierarchy flag is ON, populate Shop and/or
    // Planner from the nearest ancestor equipment that has a value, if the base
    // equipment itself does not supply one.
    let workOrderTemplate = response.data.WorkOrder;
    if (workOrderTemplate) {
      const woControlResponse = await getWorkOrderControl();
      if (woControlResponse?.data?.DefaultFromEqHierarchyFlag) {
        if (!workOrderTemplate.Shop || !workOrderTemplate.Planner) {
          const shopPlannerResponse = await getParentShopAndPlanner({
            equipment: decodeParam(equipId),
          });
          if (shopPlannerResponse?.status === 200) {
            workOrderTemplate = {
              ...workOrderTemplate,
              Shop:
                !workOrderTemplate.Shop && shopPlannerResponse.data?.Shop
                  ? shopPlannerResponse.data.Shop
                  : workOrderTemplate.Shop,
              Planner:
                !workOrderTemplate.Planner && shopPlannerResponse.data?.Planner
                  ? shopPlannerResponse.data.Planner
                  : workOrderTemplate.Planner,
            };
          }
        }
      }
    }

    // US 820773: a Standard Request has WoType "Standard Request", but the WO
    // created from it must be a normal (Routine) work order — not another SR —
    // so the backend copies field values while linking back via SrBase. Force
    // Routine here; the user can still switch to Emergency via the toggle.
    if (srNumber && workOrderTemplate) {
      workOrderTemplate = { ...workOrderTemplate, WoType: "Routine" };
    }

    return setTemplate(workOrderTemplate);
  }, [displayWoId, equipId, isOffline, srNumber, templateWindowName]);

  const attachFollowUp = async (response: ApiResponse) => {
    attachFollowupWo(workOrderId!, response.data?.WorkOrderNumber);
  };

  useEffect(() => {
    setValue("Equipment", String(scanText));
  }, [scanText, setValue]);

  // US 820773: gate the "Copy from Standard Request" field on SR-create access.
  useEffect(() => {
    getCreateWorkOrderFromSrAccess().then(setHasSrAccess);
  }, []);

  // Show the SR field only for the (online) technician WO-create flow — not for
  // the requester flow, and not offline (the SR list needs a live query).
  const showSrField =
    hasSrAccess && !isOffline && templateWindowName !== "wr";

  const onSrSelected = (sr: string) => {
    setShowSrModal(false);
    // Re-enter this page with the SR route param so the existing SR pre-fill
    // path (template + tasks + SrBase) runs.
    history.replace(
      `/standard-request/${encodeParam(sr)}/equipment/${encodeParam(
        displayEquipId
      )}/create`
    );
  };

  const getWorkOrderTypeAccess = useCallback(async () => {
    const emergencyAccess = await getUserHasAccess(
      "w_wo_book_eu_main",
      "Work Order Tab Emergency New"
    );
    setEmergencyAccess(emergencyAccess);
    if (!emergencyAccess) setValue("WoType", "Routine");
  }, [setValue]);

  useEffect(() => {
    afterFilterData();
  }, [filter]);

  useEffect(() => {
    setValues((prev) => ({
      ...prev,
      ...(assignedTo !== "" ? { AssignedTo: assignedTo } : {}),
      ...(nextReviewer !== "" ? { NextReviewer: nextReviewer } : {}),
      ...(requestor !== "" ? { Requestor: requestor } : {}),
      ...technicians.reduce((acc, technician, index) => {
        if (technician !== "") {
          acc[`Technician${index + 1}`] = technician;
        }
        return acc;
      }, {} as { [key: string]: string })
    }));
  }, [assignedTo, nextReviewer, requestor, technicians]);

  useIonViewDidLeave(() => {
    //reset value on page close
    setValues({});
    setCustomValues({});
    setIsEmergency(false);
    setIsDown(false); // US 872838: reset Down flag on page close
  });

  const getMetaData = useCallback(async () => {
    if (createWoMetaData) return; //got it from the cache
    const response = await getCreateWoMetaData();
    if (response.status !== 200) setMetadataError(true);
  }, [createWoMetaData, getCreateWoMetaData]);

  const getCustomFields = useCallback(async () => {
    if (customFields) return; //got it from cache
    await getCustomColumns();
  }, [customFields, getCustomColumns]);

  // US 820773: fetch the SR's tasks/subtasks so they can be copied onto the new
  // WO. GetTasksList returns the SR header row first (index 0) followed by its
  // child tasks/subtasks; we copy the children. A non-empty list is what tells
  // the backend (CreateWorkOrderForEquipment) to run the SR task/subtask copy.
  const loadSrTasks = useCallback(async () => {
    if (!srNumber) {
      setSrTaskSubtaskList([]);
      return;
    }
    try {
      const response = await getTasksList(srNumber);
      const list = Array.isArray(response.data) ? response.data : [];
      const tasks = list
        .slice(1)
        .map((t: any) => ({ WoTask: t.WoTask, WoSubtask: t.WoSubtask }));
      setSrTaskSubtaskList(tasks);
    } catch {
      setSrTaskSubtaskList([]);
    }
  }, [srNumber]);

  const setupData = useCallback(async () => {
    // US 906024 - the required flags ride along in getCascadedValues, so a session that cached
    // the values before this release still picks the flags up.
    if (!allCascadeValues || !allCascadeRequiredValues) await getCascadedValues();
    await getWorkOrderTypeAccess();
    await getTemplate();
    await loadSrTasks();
    await getCustomFields();
    await getMetaData();
  }, [
    allCascadeValues,
    allCascadeRequiredValues,
    getCascadedValues,
    getCustomFields,
    getMetaData,
    getTemplate,
    getWorkOrderTypeAccess,
    loadSrTasks,
  ]);

  useEffect(() => {
    if (!createWoMetaData) return;
    const offlineWo = offlineWoId
      ? useWoOfflineUpdatesStore.getState().getWorkOrderById(offlineWoId)
      : undefined;

    // Set metadata and values
    let fields = [] as any[];
    let logValues = {} as any;
    let errorVals = {} as any;
    let assignedToValidData: ValidValues[] = [];
    let tempReviewerValidData: ValidValues[] = [];
    let tempRequestorValidData: ValidValues[] = [];
    let tempTechnicianValidData = Array(6).fill([]);

    const keys = Object.keys(createWoMetaData.fields);
    keys.forEach((key) => {
      fields.push(createWoMetaData?.fields[key]);
      let currField = createWoMetaData?.fields[key];
      let propName = currField.PropertyName;
      let elementType = currField.ElementType;
      if (propName === "AssignedTo") {
        setAssignedTo(offlineWo?.requestObject[propName] ?? "");
        if (elementType === "Dropdown")
          assignedToValidData = currField.ValidValues;
      }
      if (propName === "NextReviewer") {
        setNextReviewer(offlineWo?.requestObject[propName] ?? "");
        if (elementType === "Dropdown")
          tempReviewerValidData = currField.ValidValues;
      }
      if (propName === "Requestor") {
        setRequestor(offlineWo?.requestObject[propName] ?? "");
        if (elementType === "Dropdown")
          tempRequestorValidData = currField.ValidValues;
      }
      if (/^Technician\d+$/.test(propName) && elementType === "Dropdown") {
        const match = propName.match(/^Technician(\d+)$/);
        const index = match ? parseInt(match[1], 10) - 1 : -1;
        if (index >= 0) {
          tempTechnicianValidData[index] = currField.ValidValues ?? [];
        }
      }
      if (!!template && Object.hasOwn(template, propName)) {
        // If we have the template and it has the relevant property, use the template's property value to set the default value
        logValues[propName] = offlineWo
          ? offlineWo.requestObject[propName] ?? template[propName]
          : template[propName];
      } else {
        if (propName === "WoType") {
          logValues[propName] = "Routine";
        } else if (propName === "Equipment") {
          logValues[propName] = equipId;
        } else if (createWoMetaData?.fields[key].NetType === "Boolean") {
          logValues[propName] = false;
        } else {
          logValues[propName] = offlineWo
            ? offlineWo.requestObject[propName] ?? ""
            : "";
        }
      }

      if (createWoMetaData?.fields[key].IsRequired) {
        errorVals[propName] = false;
      }
    });
    setAssignedToValidData(assignedToValidData);
    setReviewerValidData(tempReviewerValidData);
    setRequestorValidData(tempRequestorValidData);
    setFilteredAssignToList(assignedToValidData);
    setFilteredReviewerList(tempReviewerValidData);
    setFilteredRequestorList(tempRequestorValidData);
    setFilteredTechnicianLists(tempTechnicianValidData);
    setTechnicianValidData(tempTechnicianValidData);

    fields.sort((a, b) => {
      return Number(a.FieldOrder) - Number(b.FieldOrder);
    });

    dependencyMap.current = getCascadeDependencyMap(fields);
    /* US 906024 - the screen can open with cascade parents already answered (a template, a
       Standard Request, or an offline work order being resumed), so the required rules have to
       be evaluated once on load as well as on every change - the EAM Web client does the same
       from reApplyCascadeFilters. Done as one pass over the seeded values rather than per
       field, so the metadata is written once. */
    getCascadeRequiredValuesForPlant().then((requiredValues) => {
      if (!requiredValues.length) return;
      const requiredChildFields = combinedFields.reduce(
        (names: string[], parent: any) => {
          const child = findCascadeChild(parent, combinedFields);
          if (
            child?.PropertyName &&
            isCascadeChildRequired(
              requiredValues,
              parent.PropertyName,
              logValues[parent.PropertyName ?? ""],
              logValues[parent.CascadeParent ?? ""]
            )
          ) {
            names.push(child.PropertyName);
          }
          return names;
        },
        []
      );
      if (!requiredChildFields.length) return;
      setMetadata((pre) =>
        pre.map((field) =>
          requiredChildFields.includes(field.PropertyName ?? "")
            ? { ...field, CascadeRequired: true }
            : field
        )
      );
    });
    getStoreAccountCascadeValues(fields.map((f) => f.PropertyName ?? "")).then(
      (cascadeVals) => {
        setCascadeValues(cascadeVals);
        // US 835011 / Bug 887871: WO Type is a toggle, so its change never runs
        // the combo cascade path. Pre-filter its mapped child (Priority) for the
        // default WO Type value so the dropdown honors the field mapping on open.
        const woTypeField = combinedFields.find(
          (f) => f.PropertyName === "WoType"
        );
        if (woTypeField) {
          const parentName = woTypeField.PropertyName ?? "";
          const defaultWoType = logValues[parentName];
          const child = findCascadeChild(woTypeField, combinedFields);
          if (child && cascadeVals[parentName]?.length && defaultWoType) {
            updateCascadeChildOptions(
              child,
              cascadeVals[parentName],
              defaultWoType,
              setMetadata
            );
          }
        }
      }
    );

    const offlineUserDefinedValues =
      offlineWo?.requestObject?.UserDefinedValues;
    const offlineCustomValues = Array.isArray(offlineUserDefinedValues)
      ? Object.fromEntries(
          offlineUserDefinedValues.map((item: any) => {
            const element = item.Element || item.IdText;
            const value =
              item.Value ??
              item.StringValue ??
              item.NumericValue ??
              item.DateValue ??
              "";
            return [element, value];
          })
        )
      : {};
    const preparedCustomFields = Array.isArray(customFields)
      ? customFields
          .filter((field: any) => field.IsUsed !== false)
          .map((field: any) => {
            const propertyName = getCustomFieldKey(field);
            const value =
              offlineCustomValues[field.IdText] ?? field.ValueToDefault ?? "";
            logValues[propertyName] = value;
            if (field.IsRequired) errorVals[propertyName] = false;

            return {
              ...field,
              IsCustomField: true,
              UdvPropertyName: field.PropertyName,
              PropertyName: propertyName,
            };
          })
      : [];
    setCustomValues(
      Object.fromEntries(
        preparedCustomFields
          .map((field: any) => [field.IdText, logValues[field.PropertyName]])
          .filter(([, value]) => value !== "" && value != null)
      )
    );
    const combinedFields = [...fields, ...preparedCustomFields];
    setMetadata(combinedFields);

    setValues(logValues);
    setShowError(errorVals);
  }, [
    createWoMetaData,
    customFields,
    equipId,
    isOffline,
    offlineWoId,
    template,
  ]);

  useEffect(() => {
    (async () => {
      setShowLoading(true);
      await setupData();
      setShowLoading(false);
    })();
  }, [setupData]);

  const handleCustomValues = (
    field: string,
    propertyName: string,
    value: any
  ) => {
    setCustomValues((current) => ({ ...current, [field]: value }));
    setValue(propertyName, value);
  };

  const refreshCustomFields = async (workClass: string) => {
    setShowLoading(true);
    try {
      const refreshedFields = await getCustomColumns(workClass, false);
      if (!Array.isArray(refreshedFields)) return;

      const preparedFields = refreshedFields
        .filter((field: any) => field.IsUsed !== false)
        .map((field: any) => ({
          ...field,
          IsCustomField: true,
          UdvPropertyName: field.PropertyName,
          PropertyName: getCustomFieldKey(field),
        }));

      setMetadata((current) => [
        ...current.filter((field) => !isCustomField(field)),
        ...preparedFields,
      ]);
      setValues((current) => {
        const nextValues = Object.fromEntries(
          Object.entries(current).filter(
            ([key]) => !key.startsWith(CUSTOM_FIELD_PREFIX)
          )
        );
        preparedFields.forEach((field: any) => {
          nextValues[field.PropertyName] = field.ValueToDefault ?? "";
        });
        return nextValues;
      });
      setCustomValues(
        Object.fromEntries(
          preparedFields
            .map((field: any) => [field.IdText, field.ValueToDefault])
            .filter(([, value]) => value !== "" && value != null)
        )
      );
      setShowError((current: any) => {
        const nextErrors = Object.fromEntries(
          Object.entries(current).filter(
            ([key]) => !key.startsWith(CUSTOM_FIELD_PREFIX)
          )
        );
        preparedFields.forEach((field: any) => {
          if (field.IsRequired) nextErrors[field.PropertyName] = false;
        });
        return nextErrors;
      });
    } finally {
      setShowLoading(false);
    }
  };

  const enteriesInvalid = () => {
    let requiredEntries = [] as string[];
    metadata.forEach((meta) => {
      // US 906024 - includes fields a field mapping has made mandatory for the selected code.
      if (isFieldRequired(meta)) {
        requiredEntries.push(meta.PropertyName);
      }
    });
    if (requiredEntries.length === 0) {
      return false;
    } else {
      let requiredAbsent = !requiredEntries.every((req) => {
        return (
          values[req] !== null &&
          values[req] !== undefined &&
          values[req] !== ""
        );
      });
      console.log("Absent Value", requiredAbsent);
      return requiredAbsent;
    }
  };

  async function submitWorkOrder() {
    let temp = { ...values };
    const keys = Object.keys(temp);
    keys.forEach((key) => {
      if (key.startsWith(CUSTOM_FIELD_PREFIX)) {
        delete temp[key];
        return;
      }
      if (temp[key] === "") {
        temp[key] = null;
      }
    });

    temp.CopyFromAppId = "Mobile";
    temp.Equipment = displayEquipId;
    if (workOrderId && displayWoId) {
      temp.FollowUpToWoNumber = displayWoId;
    }
    // US 820773: link the new WO to its source Standard Request and carry the
    // SR's tasks/subtasks. SrBase drives the server-side copy; a non-empty
    // SRTaskSubtaskList is required for the task/subtask copy to run.
    if (srNumber) {
      temp.SrBase = srNumber;
      temp.SRTaskSubtaskList = srTaskSubtaskList;
    }
    if (attachedDocuments.length > 0) {
      temp.Documents = attachedDocuments;
    }
    const userDefinedFields = metadata.filter(isCustomField);
    if (userDefinedFields.length > 0) {
      temp.UserDefinedValues = userDefinedFields.map((field) => {
        const value = values[field.PropertyName];
        return {
          Category: field.Category || "Work Order-All",
          Subcategory: field.Subcategory || " ",
          Element: field.Element || field.IdText,
          [field.UdvPropertyName]:
            value === "" || value === undefined ? null : value,
        };
      });
    }
    if (templateWindowName === "wr") {
      temp.IsWorkRequester = true;
    }
    temp.NotebookTranslations = {
      "wo_work_order.description": [
        {
          TableName: "wo_work_order",
          ElementName: "description",
          Language: "Default",
          PlainText: temp.Description,
          RichText: null,
        },
      ],
    };
    sendCreateViaCopy(temp);
  }

  const sendCreateViaCopy = async (
    requestObject: any,
    ruleConfirmations?: any[]
  ) => {
    const data = {
      Confirmation:
        ruleConfirmations && ruleConfirmations.length > 0
          ? { RuleConfirmationDetailsList: ruleConfirmations }
          : {},
      RequestObject: requestObject,
    };
    if (isOffline) {
      const wo = {
        confirmation: data.Confirmation,
        requestObject: data.RequestObject,
        followupWoNumber: displayWoId,
      };
      offlineWoId ? updateById(offlineWoId, wo) : addWorkOrderToCreate(wo);
      presentAlert({
        header: `${translations["lbl_success"] || "Success"}!`,
        message:
          translatedMessages["MobOfflineWoCreated"]?.MessageText ||
          "Work order added in offline storage.",
        onDidDismiss: () => {
          history.replace(`/home`);
        },
        buttons: ["OK"],
      });
      return; // we will sync it later
    }

    setShowLoading(true);

    const response = await createWo({
      confirmation: data.Confirmation,
      requestObject: data.RequestObject,
    });
    if (response.status === 200 && response.data.Success) {
      // workOrderId is true if coming from completion screen and has follow up flag enabled
      !!workOrderId && attachFollowUp(response);
      presentAlert({
        header: `${translations["lbl_success"] || "Success"}!`,
        message: response.data.MessageList[0].Text,
        onDidDismiss: () => {
          setShowLoading(true);
          setMetadata([]);
          setValues({});
          setCustomValues({});
          setShowError({});
          setAttachedDocuments([]);
          setMetadataError(false);
          if (offlineWoId) deleteById(offlineWoId);
          history.replace(
            `${routeForward}/${encodeParam(response.data.WorkOrderNumber)}`,
            { prevUrl: window.location.href }
          );
        },
        buttons: ["OK"],
      });
    } else {
      handleApiFailResponse(response, requestObject);
    }
    setShowLoading(false);
  };

  // US 820773: Present the backend's business-rule Yes/No questions one at a
  // time. Answering "No"/Abort to any question cancels the whole create (matches
  // the Web SR copy flow); answering "Yes"/Continue to all re-posts with the
  // accumulated confirmations. The re-post may itself return further questions,
  // which flow back through handleApiFailResponse and are prompted again.
  const promptRuleConfirmations = (
    requestObject: any,
    questions: any[],
    index: number,
    confirmations: any[]
  ) => {
    if (index >= questions.length) {
      sendCreateViaCopy(requestObject, confirmations);
      return;
    }
    const question = questions[index];
    const continueResponse = question.ValidResponses?.find(
      (r: any) => r.Action === "Continue"
    );
    const abortResponse = question.ValidResponses?.find(
      (r: any) => r.Action === "Abort"
    );
    presentAlert({
      header: question.Text,
      backdropDismiss: false,
      buttons: [
        {
          text: abortResponse?.DisplayText || translations["lbl_btn_no"] || "No",
          role: "cancel",
        },
        {
          text:
            continueResponse?.DisplayText || translations["lbl_btn_yes"] || "Yes",
          handler: () => {
            promptRuleConfirmations(requestObject, questions, index + 1, [
              ...confirmations,
              {
                MessageId: question.Id,
                MessageConfirmationValue: {
                  val1: continueResponse?.ResponseValue || "Y",
                },
              },
            ]);
          },
        },
      ],
    });
  };

  const handleApiFailResponse = async (
    response: ApiResponse,
    requestObject: any
  ) => {
    const messageList = response.data?.MessageList ?? [];

    // Business-rule Yes/No questions the backend wants confirmed before it will
    // create the WO (e.g. warranty, SR copy rules). Prompt them instead of
    // treating the response as an outright failure.
    const questions = messageList.filter((m: any) => m.MessageType === 1);
    if (questions.length > 0) {
      promptRuleConfirmations(requestObject, questions, 0, []);
      return;
    }

    // Otherwise it is a genuine failure — show the messages and stay on page.
    const message = messageList
      .map((m: any) => m.Text)
      .filter(Boolean)
      .join("\n");
    const requestWorkOrder = pathname.includes("requester")
      ? "Request"
      : "Work Order";
    presentAlert({
      header:
        translatedMessages["WOCreateFailed"]?.MessageText.replace(
          "Work Order",
          requestWorkOrder
        ) || `${requestWorkOrder} creation failed`,
      message: message,
      buttons: ["OK"],
    });
  };

  const selectFile = async () => {
    // invoke the hidden input's click event
    (document as any).getElementById("filePickerWO").click();
  };

  const fileClicked = async () => {
    (document as any).getElementById("filePickerWO").value = null;
  };

  const takePhoto = async () => {
    const source = await presentCustomPhotoSourceDialog();
    if (!source) return;

    if (source === CameraSource.Photos) {
      const picked = await pickImageFromGallery();
      if (!picked) return;

      presentAlert({
        message:
          translations["lbl_enter_file_name"] || "Please enter file name",
        backdropDismiss: false,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            async handler(value) {
              if (!!value && value[0] !== "") {
                setShowLoading(true);
                const base64Data = (await convertBlobToBase64(
                  picked.file
                )) as string;
                const base64Array = base64Data.split(";base64,");

                let newPhoto = {
                  FileName: picked.extension
                    ? `${value[0]}.${picked.extension}`
                    : value[0],
                  DocContentBase64: base64Array[1] || "",
                  File: picked.file,
                };

                let allFiles = attachedDocuments.slice();
                allFiles.push(newPhoto);
                setAttachedDocuments(allFiles);
                setShowLoading(false);
                return;
              }
              return false;
            },
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "Cancel",
          },
        ],
        inputs: [
          {
            placeholder: translations["lbl_file_name"] || "File Name",
            attributes: {
              maxlength: 100,
            },
          },
        ],
      });
      return;
    }


    await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      allowEditing: false,
      quality: 100,
      source: source as CameraSource,
    }).then(async (photo) => {
      presentAlert({
        message:
          translations["lbl_enter_file_name"] || "Please enter file name",
        backdropDismiss: false,
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
            async handler(value) {
              if (!!value && value[0] !== "") {
                setShowLoading(true);
                let newPhoto = {
                  FileName: `${value[0]}.${photo.format}`,
                  DocContentBase64: "",
                  File: photo,
                };
                let base64Data = await readDataAsBase64(photo);
                if (isPlatform("hybrid")) {
                  newPhoto.DocContentBase64 = base64Data;
                } else {
                  newPhoto.DocContentBase64 = base64Data.split(";base64,")[1];
                }
                let allFiles = attachedDocuments.slice();
                allFiles.push(newPhoto);
                setAttachedDocuments(allFiles);
                setShowLoading(false);
                return;
              }
              return false;
            },
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "Cancel",
          },
        ],
        inputs: [
          {
            placeholder: translations["lbl_file_name"] || "File Name",
            attributes: {
              maxlength: 100,
            },
          },
        ],
      });
    });
  };

  const presentCustomPhotoSourceDialog = async () => {
    const promise = new Promise<CameraSource | null>((resolve) => {
      presentAlert({
        header: "Choose Photo Source",
        message: "Do you want to take a new photo or select an existing one?",
        buttons: [
          {
            text: "Take Picture",
            handler: () => {
              console.log("Take Picture clicked");
              resolve(CameraSource.Camera);
            },
          },
          {
            text: "From Photos",
            handler: () => {
              console.log("From Photos clicked");
              resolve(CameraSource.Photos);
            },
          },
          {
            text: "Cancel",
            role: "cancel",
            handler: () => {
              console.log("Cancel clicked");
              resolve(null);
            },
          },
        ],
      });
    });

    return promise;
  };

  const fileSelected = async (event: any) => {
    let newDoc = {
      FileName: "",
      DocContentBase64: "",
      File: null as File | null,
    };
    let uploadedFile = event.target.files[0];
    presentAlert({
      message: translations["lbl_enter_file_name"] || "Please enter file name",
      backdropDismiss: false,
      buttons: [
        {
          text: translations["lbl_btn_ok"] || "OK",
          async handler(value) {
            if (!!value && value[0] !== "") {
              setShowLoading(true);
              newDoc.File = uploadedFile;
              newDoc.FileName = uploadedFile.name;
              let base64Data = (await convertBlobToBase64(
                uploadedFile
              )) as string;
              let base64Array = base64Data.split(";base64,");
              newDoc.DocContentBase64 = base64Array[1];
              let allFiles = attachedDocuments.slice();
              allFiles.push(newDoc);
              setAttachedDocuments(allFiles);
              event.target.value = "";
              setShowLoading(false);
              return;
            }
            return false;
          },
        },
        {
          text: translations["lbl_btn_cancel"] || "Cancel",
          role: "Cancel",
        },
      ],
      inputs: [
        {
          id: "txtFileName",
          placeholder: translations["lbl_file_name"] || "File Name",
          attributes: {
            maxlength: 100,
          },
          value: uploadedFile.name,
        },
      ],
      onDidPresent(event) {
        //pre-select the name so that its easier for the user to edit
        (document.getElementById("txtFileName") as HTMLInputElement).select();
      },
    });
  };

  const removeFile = (fileIndex: number) => {
    let docsCopy = attachedDocuments.slice();
    docsCopy.splice(fileIndex, 1);
    setAttachedDocuments(docsCopy);
  };

  const handleEmployeeSearch = (e: CustomEvent, cardPropertyName: string) => {
    let newVal = (e.target as HTMLInputElement)?.value;
    setPropertyName(cardPropertyName);
    if (cardPropertyName === "NextReviewer") {
      setNextReviewer(newVal);
    } else if (cardPropertyName === "Requestor") {
      setRequestor(newVal);
    } else if (cardPropertyName === "AssignedTo") {
      setAssignedTo(newVal);
    }
    const technicianMatch = cardPropertyName.match(/^Technician(\d+)$/);
    if (technicianMatch) {
      const index = parseInt(technicianMatch[1], 10) - 1;
      const updatedTechnicians = [...technicians];
      updatedTechnicians[index] = newVal;
      setTechnicians(updatedTechnicians);
    }
    if (newVal.length > 2) {
      findEmployee(newVal).then((response) => {
        if (response.status === 200) {
          if (Object.keys(response.data).length !== 0) {
            popover.current!.event = e;
            setPopoverOpen(true);
          } else if (popoverOpen) {
            setPopoverOpen(false);
          }
          let empSearchArray = [];
          const employees = Object.entries(response.data).map(([id, name]) => ({
            EmployeeId: id,
            EmployeeName: name,
          }));
          for (let search of employees) {
            empSearchArray.push(search.EmployeeId);
          }
          setSearchResults(empSearchArray);
        }
      });
    } else {
      if (popoverOpen) {
        setPopoverOpen(false);
      }
      if (searchResults.length > 0) {
        setSearchResults([]);
      }
    }
  };

  const resetSearchState = () => {
    setFilterModalColumnName("");
    setFilterModalModuleName("");
  };

  const onFilterModalConfirm = (
    selectedItem: string | number | null | undefined,
    currentPropertyName: string | null | undefined = null
  ) => {
    setParameter(filterModalColumnName, selectedItem, currentPropertyName);
    setIsFilterModalOpen(false);
    resetSearchState();
  };

  const setParameter = (
    paramKey: string,
    paramValue: string | number | null | undefined,
    currPropertyName: string | null
  ) => {
    const effectivePropertyName = currPropertyName || propertyName;
    if (effectivePropertyName === "NextReviewer") {
      setNextReviewer(paramValue as string);
    } else if (effectivePropertyName === "Requestor") {
      setRequestor(paramValue as string);
    } else if (effectivePropertyName === "AssignedTo") {
      setAssignedTo(paramValue as string);
    }
    const technicianMatch = effectivePropertyName.match(/^Technician(\d+)$/);
    if (technicianMatch) {
      const index = parseInt(technicianMatch[1], 10) - 1;
      const updatedTechnicians = [...technicians];
      updatedTechnicians[index] = paramValue as string;
      setTechnicians(updatedTechnicians);
    }
    if (effectivePropertyName) {
      setValue(effectivePropertyName, (paramValue as string) ?? "");
    }
  };

  const handleClose = (e: any, card: any) => {
    if (card.PropertyName === "NextReviewer") {
      setSelectedReviewer(selectedReviewer);
      setShowNextReviewerModal(false);
    } else if (card.PropertyName === "Requestor") {
      setSelectedRequestor(selectedReviewer);
      setShowRequestorModal(false);
    } else if (card.PropertyName === "AssignedTo") {
      // setSelectedAssignTo(selectedAssignTo);
      setShowAssignedToModal(false);
    }
    const technicianMatch = card.PropertyName.match(/^Technician(\d+)$/);
    if (technicianMatch) {
      const index = parseInt(technicianMatch[1], 10) - 1;
      const updatedSelectedTechnicians = [...selectedTechnicians];
      updatedSelectedTechnicians[index] = selectedTechnicians[index];
      setSelectedTechnicians(updatedSelectedTechnicians);

      const updatedShowTechnicianModals = [...showTechnicianModals];
      updatedShowTechnicianModals[index] = false;
      setShowTechnicianModals(updatedShowTechnicianModals);
    }
    setFilter("");
  };

  const handleRadioChange = (e: any, card: any) => {
    if (card.PropertyName === "NextReviewer") {
      setSelectedReviewer(e.detail.value);
    } else if (card.PropertyName === "Requestor") {
      setSelectedRequestor(e.detail.value);
    } else if (card.PropertyName === "AssignedTo") {
      setSelectedAssignTo(e.detail.value);
    }
    const technicianMatch = card.PropertyName.match(/^Technician(\d+)$/);
    if (technicianMatch) {
      const index = parseInt(technicianMatch[1], 10) - 1;
      const updatedSelectedTechnicians = [...selectedTechnicians];
      updatedSelectedTechnicians[index] = e.detail.value;
      setSelectedTechnicians(updatedSelectedTechnicians);
    }
  };

  const handleOk = (e: any, card: any) => {
    if (card.PropertyName === "NextReviewer") {
      setNextReviewer(selectedReviewer);
      setShowNextReviewerModal(false);
    } else if (card.PropertyName === "Requestor") {
      setRequestor(selectedRequestor);
      setShowRequestorModal(false);
    } else if (card.PropertyName === "AssignedTo") {
      setAssignedTo(selectedAssignTo);
      setShowAssignedToModal(false);
    } else if (/^Technician\d+$/.test(card.PropertyName)) {
      const match = card.PropertyName.match(/^Technician(\d+)$/);
      const index = match ? parseInt(match[1], 10) - 1 : -1;
      if (index >= 0) {
        const updatedTechnicians = [...technicians];
        updatedTechnicians[index] = selectedTechnicians[index];
        setTechnicians(updatedTechnicians);

        const updatedShowTechnicianModals = [...showTechnicianModals];
        updatedShowTechnicianModals[index] = false;
        setShowTechnicianModals(updatedShowTechnicianModals);
      }
    }
    setFilter("");
  };

  const afterFilterData = () => {
    if (propertyName === "NextReviewer") {
      if (filter === "") {
        setFilteredReviewerList(reviewerValidData);
      } else {
        setFilteredReviewerList(
          reviewerValidData?.filter(
            (card) =>
              card.Code.toLowerCase().includes(filter.toLowerCase()) ||
              card.Description.toLowerCase().includes(filter.toLowerCase())
          )
        );
      }
    } else if (propertyName === "Requestor") {
      if (filter === "") {
        setFilteredRequestorList(requestorValidData);
      } else {
        setFilteredRequestorList(
          requestorValidData?.filter(
            (card) =>
              card.Code.toLowerCase().includes(filter.toLowerCase()) ||
              card.Description.toLowerCase().includes(filter.toLowerCase())
          )
        );
      }
    } else if (propertyName === "AssignedTo") {
      if (filter === "") {
        setFilteredAssignToList(assignedToValidData);
      } else {
        setFilteredAssignToList(
          assignedToValidData?.filter(
            (card) =>
              card.Code.toLowerCase().includes(filter.toLowerCase()) ||
              card.Description.toLowerCase().includes(filter.toLowerCase())
          )
        );
      }
    } else if (/^Technician\d+$/.test(propertyName)) {
      const match = propertyName.match(/^Technician(\d+)$/);
      const index = match ? parseInt(match[1], 10) - 1 : -1;
      if (filter === "") {
        const updatedFilteredTechnicianLists = [...filteredTechnicianLists];
        const technicianData = technicianValidData[index];
        updatedFilteredTechnicianLists[index] = Array.isArray(technicianData)
          ? technicianData
          : [];
        setFilteredTechnicianLists(updatedFilteredTechnicianLists);
      } else {
        const updatedFilteredTechnicianLists = [...filteredTechnicianLists];
        const technicianData = technicianValidData[index];
        updatedFilteredTechnicianLists[index] = Array.isArray(technicianData)
          ? technicianData.filter(
              (card: ValidValues) =>
                card.Code?.toLowerCase().includes(filter.toLowerCase()) ||
                card.Description?.toLowerCase().includes(filter.toLowerCase())
            )
          : [];
        setFilteredTechnicianLists(updatedFilteredTechnicianLists);
      }
    }
  };

  // US 872838: The logged-in technician becomes the Assigned Technician only
  // when BOTH "Create as Emergency" and the "Down" (downtime) flag are on, so
  // downtime WOs always have an owner. Turning either flag off clears the
  // field. No-op when the Assigned Technician field is not on the form.
  const applyDowntimeAutoAssign = (emergency: boolean, down: boolean) => {
    const hasAssignedToField = metadata.some(
      (m) => m.PropertyName === "AssignedTo"
    );
    if (!hasAssignedToField) return;
    if (emergency && down) {
      getUserId().then((uid) => {
        if (uid) setAssignedTo(uid);
      });
    } else {
      setAssignedTo("");
      setValue("AssignedTo", "");
    }
  };

  const renderFields = (fields: any[]) =>
    fields.map((meta: any, index: number) => {
          if (meta.PropertyName === "WoType" && !emergencyAccess) {
            return null;
          }
          //do not show this, if user is not updating offline wo or adding wo in offline
          if (meta.PropertyName === "Equipment" && !isOffline && !offlineWoId) {
            return null;
          }
          // US 906024 - the field's own setting OR a cascade rule; never the other way round,
          // so a field mapping can add required-ness but can never take it away.
          let required = isFieldRequired(meta);
          const calculateInput = () => {
            if (meta.PropertyName === "WoType") {
              return (
                <IonToggle
                  slot="end"
                  checked={isEmergency}
                  onIonChange={(ev) => {
                    const nextChecked = ev.detail.checked;
                    if (nextChecked === isEmergency) return;
                    setIsEmergency(nextChecked);
                    const woTypeValue = nextChecked ? "Emergency" : "Routine";
                    // US 835011 / Bug 887871: WO Type renders as a toggle, so it
                    // bypasses the combo cascade path. Re-filter the mapped child
                    // (Priority) for the new WO Type and clear any dependent
                    // selection that is no longer valid under the mapping.
                    const cascadedDataToReset = applyCascadeToChildren(
                      meta,
                      woTypeValue
                    );
                    setValues((pre: any) => ({
                      ...pre,
                      ...cascadedDataToReset,
                      [meta.PropertyName]: woTypeValue,
                    }));
                    // US 872838: auto-assign the logged-in technician only when
                    // BOTH Emergency and Down are on; otherwise clear it.
                    applyDowntimeAutoAssign(nextChecked, isDown);
                  }}
                ></IonToggle>
              );
            }
            if (meta.PropertyName === "UpDownFlag") {
              // US 872838: "Down" (downtime) flag. Own state + echo guard mirror
              // the Emergency toggle so adding the auto-assign side effect does
              // not make the controlled IonToggle bounce.
              return (
                <IonToggle
                  slot="end"
                  checked={isDown}
                  onIonChange={(ev) => {
                    const nextChecked = ev.detail.checked;
                    if (nextChecked === isDown) return;
                    setIsDown(nextChecked);
                    setValue(meta.PropertyName, nextChecked);
                    // US 872838: auto-assign requires BOTH Emergency and Down.
                    applyDowntimeAutoAssign(isEmergency, nextChecked);
                  }}
                ></IonToggle>
              );
            }
            if (meta.PropertyName === "Equipment") {
              return (
                <IonItem class="wo-editable-container">
                  <IonInput
                    class="wo-editable-input-assigned"
                    onIonInput={(ev) => {
                      setValue(meta.PropertyName, String(ev.target.value));
                    }}
                    value={values[meta.PropertyName]}
                  ></IonInput>
                  <IonIcon
                    slot="end"
                    ios={barcodeOutline}
                    md={barcodeSharp}
                    onClick={() => scanClick()}
                  ></IonIcon>
                </IonItem>
              );
            } else if (
              (meta.PropertyName === "NextReviewer" ||
                meta.PropertyName === "AssignedTo" ||
                meta.PropertyName === "Requestor" ||
                /^Technician\d+$/.test(meta.PropertyName)) &&
              meta.ElementType
            ) {
              if (meta.ElementType === "Dropdown") {
                const technicianMatch =
                  meta.PropertyName.match(/^Technician(\d+)$/);
                const technicianIndex = technicianMatch
                  ? parseInt(technicianMatch[1], 10) - 1
                  : -1;
                return (
                  <React.Fragment>
                    <IonItem
                      class="dropdown-item"
                      onClick={() => {
                        setPropertyName(meta.PropertyName);
                        if (meta.PropertyName === "NextReviewer") {
                          setShowNextReviewerModal(true);
                        } else if (meta.PropertyName === "AssignedTo") {
                          setShowAssignedToModal(true);
                        } else if (meta.PropertyName === "Requestor") {
                          setShowRequestorModal(true);
                        } else {
                          const index =
                            parseInt(
                              meta.PropertyName.match(/^Technician(\d+)$/)[1],
                              10
                            ) - 1;
                          const updatedShowTechnicianModals = [
                            ...showTechnicianModals,
                          ];
                          updatedShowTechnicianModals[index] = true;
                          setShowTechnicianModals(updatedShowTechnicianModals);
                        }
                      }}
                    >
                      <IonLabel slot="start" className="align-value">
                        {meta.PropertyName === "NextReviewer"
                          ? nextReviewer && nextReviewer !== ""
                            ? nextReviewer
                            : values[meta.PropertyName]
                          : meta.PropertyName === "AssignedTo"
                          ? assignedTo && assignedTo !== ""
                            ? assignedTo
                            : values[meta.PropertyName]
                          : meta.PropertyName === "Requestor"
                          ? requestor && requestor !== ""
                            ? requestor
                            : values[meta.PropertyName]
                          : technicians[technicianIndex] &&
                            technicians[technicianIndex] !== ""
                          ? technicians[technicianIndex]
                          : values[meta.PropertyName]}{" "}
                      </IonLabel>
                      <IonIcon
                        icon={chevronDownOutline}
                        className="dropdown-icon"
                        slot="end"
                      />
                    </IonItem>
                    <IonModal
                      isOpen={
                        meta.PropertyName === "NextReviewer"
                          ? showNextReviewerModal
                          : meta.PropertyName === "AssignedTo"
                          ? showAssignedToModal
                          : meta.PropertyName === "Requestor"
                          ? showRequestorModal
                          : showTechnicianModals[
                              parseInt(
                                meta.PropertyName.match(/^Technician(\d+)$/)[1],
                                10
                              ) - 1
                            ]
                      }
                      backdropDismiss={false}
                    >
                      <IonHeader>
                        <IonToolbar>
                          <IonTitle>
                            {meta.PropertyName === "NextReviewer"
                              ? "Select Reviewer"
                              : meta.PropertyName === "AssignedTo"
                              ? "Select Assigned To"
                              : meta.PropertyName === "Requestor"
                              ? "Select Requestor"
                              : "Select Technician"}
                          </IonTitle>
                          <IonButtons slot="end">
                            <IonButton onClick={(e) => handleClose(e, meta)}>
                              Close
                            </IonButton>
                          </IonButtons>
                        </IonToolbar>
                      </IonHeader>
                      <IonContent>
                        <IonItem>
                          <IonInput
                            placeholder={
                              meta.PropertyName === "NextReviewer"
                                ? "Filter Reviewers"
                                : meta.PropertyName === "AssignedTo"
                                ? "Filter Assigned To"
                                : meta.PropertyName === "Requestor"
                                ? "Filter Requestor"
                                : "Filter Technician"
                            }
                            onIonChange={(e) => setFilter(e.detail.value!)}
                            value={filter}
                          ></IonInput>
                        </IonItem>
                        <IonRadioGroup
                          value={
                            meta.PropertyName === "NextReviewer"
                              ? selectedReviewer
                              : meta.PropertyName === "AssignedTo"
                              ? selectedAssignTo
                              : meta.PropertyName === "Requestor"
                              ? selectedRequestor
                              : selectedTechnicians[
                                  parseInt(
                                    meta.PropertyName.match(
                                      /^Technician(\d+)$/
                                    )[1],
                                    10
                                  ) - 1
                                ]
                          }
                          onIonChange={(e) => handleRadioChange(e, meta)}
                        >
                          {meta.PropertyName === "NextReviewer"
                            ? filteredReviewerList?.map((option, index) => (
                                <IonItem key={index}>
                                  <IonLabel>
                                    {getValidValueDisplayText(
                                      option,
                                      meta.DropdownDisplayMode
                                    )}
                                  </IonLabel>
                                  <IonRadio slot="start" value={option.Code} />
                                </IonItem>
                              ))
                            : meta.PropertyName === "AssignedTo"
                            ? filteredAssignToList?.map((option, index) => (
                                <IonItem key={index}>
                                  <IonLabel>
                                    {getValidValueDisplayText(
                                      option,
                                      meta.DropdownDisplayMode
                                    )}
                                  </IonLabel>
                                  <IonRadio slot="start" value={option.Code} />
                                </IonItem>
                              ))
                            : meta.PropertyName === "Requestor"
                            ? filteredRequestorList?.map((option, index) => (
                                <IonItem key={index}>
                                  <IonLabel>
                                    {getValidValueDisplayText(
                                      option,
                                      meta.DropdownDisplayMode
                                    )}
                                  </IonLabel>
                                  <IonRadio slot="start" value={option.Code} />
                                </IonItem>
                              ))
                            : filteredTechnicianLists[
                                parseInt(
                                  meta.PropertyName.match(
                                    /^Technician(\d+)$/
                                  )[1],
                                  10
                                ) - 1
                              ]?.map((option, index) => (
                                <IonItem key={index}>
                                  <IonLabel>
                                    {getValidValueDisplayText(
                                      option,
                                      meta.DropdownDisplayMode
                                    )}
                                  </IonLabel>
                                  <IonRadio slot="start" value={option.Code} />
                                </IonItem>
                              ))}
                        </IonRadioGroup>
                      </IonContent>
                      <IonFooter>
                        <IonButton
                          expand="block"
                          onClick={(e) => handleOk(e, meta)}
                        >
                          OK
                        </IonButton>
                        <IonButton
                          fill="outline"
                          expand="block"
                          onClick={(e) => handleClose(e, meta)}
                        >
                          Cancel
                        </IonButton>
                      </IonFooter>
                    </IonModal>
                  </React.Fragment>
                );
              } else if (meta.ElementType === "ISS") {
                const technicianMatch =
                  meta.PropertyName.match(/^Technician(\d+)$/);
                const technicianIndex = technicianMatch
                  ? parseInt(technicianMatch[1], 10) - 1
                  : -1;
                return (
                  <IonItem class="wo-editable-container">
                    <IonInput
                      class="wo-editable-input-assigned"
                      value={
                        meta.PropertyName === "NextReviewer"
                          ? nextReviewer && nextReviewer !== ""
                            ? nextReviewer
                            : values[meta.PropertyName]
                          : meta.PropertyName === "AssignedTo"
                          ? assignedTo && assignedTo !== ""
                            ? assignedTo
                            : values[meta.PropertyName]
                          : meta.PropertyName === "Requestor"
                          ? requestor && requestor !== ""
                            ? requestor
                            : values[meta.PropertyName]
                          : technicians[technicianIndex] &&
                            technicians[technicianIndex] !== ""
                          ? technicians[technicianIndex]
                          : values[meta.PropertyName]
                      }
                      onIonInput={(ev) => {
                        onFilterModalConfirm(
                          ev.target.value,
                          meta.PropertyName
                        );
                        handleEmployeeSearch(ev, meta.PropertyName);
                      }}
                    ></IonInput>
                    <IonIcon
                      class="search-icon"
                      slot="end"
                      ios={searchOutline}
                      md={searchSharp}
                      onClick={async () => {
                        resetSearchState();
                        setFilterModalColumnName("employee_id");
                        const response: ApiResponse =
                          await getValueSearchDetails({
                            tableName: "em_employee",
                            columnName: "employee_id",
                          });
                        if (response.status === 200) {
                          setFilterModalModuleName(
                            response.data.QueryWizardModuleName
                          );
                          setPropertyName(meta.PropertyName);
                          setIsFilterModalOpen(true);
                        }
                      }}
                    ></IonIcon>
                  </IonItem>
                );
              }
            } else if (meta.ValidValues && meta.ValidValues.length > 0) {
              let currVal = values[meta.PropertyName];
              const name = meta.PropertyName ?? "";
              let selectVal = meta.ValidValues.find((validVal: any) => {
                return validVal.Code === currVal;
              });

              return (
                <IonSelect
                  class="wo-editable-input"
                  selectedText={
                    selectVal
                      ? getValidValueDisplayText(
                          selectVal,
                          meta.DropdownDisplayMode
                        )
                      : ""
                  }
                  interface="popover"
                  value={currVal}
                  onIonChange={async (ev: any) => {
                    if (required) {
                      let temp = { ...showError };
                      if (
                        !ev.detail.value ||
                        ev.detail.value === "" ||
                        ev.detail.value.length === 0
                      ) {
                        temp[meta.PropertyName] = true;
                        setShowError(temp);
                      } else if (
                        ev.detail.value.length > 0 &&
                        showError[meta.PropertyName]
                      ) {
                        temp[meta.PropertyName] = false;
                        setShowError(temp);
                      }
                    }

                    if (isCustomField(meta)) {
                      handleCustomValues(
                        meta.IdText,
                        meta.PropertyName,
                        ev.detail.value
                      );
                    } else {
                      /* US 906024 - was an inline copy of applyCascadeToChildren; folded into
                         the shared helper so the combo path and the WO Type toggle path apply
                         the same value filtering AND the same required rules. */
                      const cascadedDataToRest = applyCascadeToChildren(
                        meta,
                        ev.detail.value
                      );

                      setValues((pre: any) => ({
                        ...pre,
                        ...cascadedDataToRest,
                        [name]: ev.detail.value,
                      }));

                      if (meta.PropertyName === "WorkClass") {
                        await refreshCustomFields(ev.detail.value);
                      }
                    }
                  }}
                  onIonCancel={(ev: any) => {
                    let temp = { ...showError };
                    if (
                      !values[meta.PropertyName] ||
                      values[meta.PropertyName] === "" ||
                      values[meta.PropertyName].length === 0
                    ) {
                      temp[meta.PropertyName] = true;
                      setShowError(temp);
                    } else if (
                      values[meta.PropertyName].length > 0 &&
                      showError[meta.PropertyName]
                    ) {
                      temp[meta.PropertyName] = false;
                      setShowError(temp);
                    }
                  }}
                >
                  {!required && (
                    <IonSelectOption
                      key={`${meta.PropertyName}-empty-select-option`}
                      value={null}
                    >
                      {" "}
                    </IonSelectOption>
                  )}
                  {/* if meta has filtered values then use that otherwise use valid values */}
                  {[...(meta.filteredValues ?? meta.ValidValues)]
                    .sort((a: any, b: any) =>
                      compareValidValuesByDisplayText(
                        a,
                        b,
                        meta.DropdownDisplayMode
                      )
                    )
                    .map((value: any, index: number) => {
                      return (
                        <IonSelectOption
                          value={value.Code}
                          key={`${meta.PropertyName}-select-option-${index}`}
                        >
                          {getValidValueDisplayText(
                            value,
                            meta.DropdownDisplayMode
                          )}
                        </IonSelectOption>
                      );
                    }
                  )}
                </IonSelect>
              );
            } else if (meta.NetType === "Boolean") {
              return (
                <IonToggle
                  slot="end"
                  checked={values[meta.PropertyName]}
                  onIonChange={(ev) => {
                    isCustomField(meta)
                      ? handleCustomValues(
                          meta.IdText,
                          meta.PropertyName,
                          ev.detail.checked
                        )
                      : setValue(meta.PropertyName, ev.detail.checked);
                  }}
                ></IonToggle>
              );
            } else if (meta.ElementType === "htmlEditor") {
              return (
                <IonTextarea
                  class="wo-editable-input"
                  autoGrow
                  required={required}
                  value={values[meta.PropertyName]}
                  onIonChange={(ev: any) => {
                    if (required) {
                      let temp = { ...showError };
                      if (ev.detail.value.length === 0) {
                        temp[meta.PropertyName] = true;
                        setShowError(temp);
                      } else if (
                        ev.detail.value.length > 0 &&
                        showError[meta.PropertyName]
                      ) {
                        temp[meta.PropertyName] = false;
                        setShowError(temp);
                      }
                    }
                    isCustomField(meta)
                      ? handleCustomValues(
                          meta.IdText,
                          meta.PropertyName,
                          ev.detail.value
                        )
                      : setValue(meta.PropertyName, ev.detail.value);
                  }}
                ></IonTextarea>
              );
            } else {
              return (
                <IonInput
                  required={required}
                  class="wo-editable-input"
                  type={meta.NetType === "DateTime" ? "date" : undefined}
                  readonly={meta.IsProtected}
                  value={values[meta.PropertyName]}
                  onIonInput={(ev: any) => {
                    if (required) {
                      let temp = { ...showError };
                      if (ev.detail.target.value.length === 0) {
                        temp[meta.PropertyName] = true;
                        setShowError(temp);
                      } else if (
                        ev.detail.target.value.length > 0 &&
                        showError[meta.PropertyName]
                      ) {
                        temp[meta.PropertyName] = false;
                        setShowError(temp);
                      }
                    }
                    isCustomField(meta)
                      ? handleCustomValues(
                          meta.IdText,
                          meta.PropertyName,
                          ev.detail.target.value
                        )
                      : setValue(meta.PropertyName, ev.detail.target.value);
                  }}
                ></IonInput>
              );
            }
          };
          const isToggleField = meta.NetType === "Boolean" || meta.PropertyName === "WoType";
          const labelText = (
            <>
              {meta.PropertyName === "WoType"
                ? translations["lbl_create Emergency"] || "Create as Emergency"
                : meta.TranslatedIdText}
              {required && "*"}
            </>
          );
          return (
            <IonItem
              lines="none"
              class="wo-list-item"
              key={`${meta.PropertyName}-${index}`}
            >
              {isToggleField ? (
                <div className="wo-toggle-row">
                  <IonLabel color={required ? "danger" : undefined}>
                    {labelText}
                  </IonLabel>
                  {calculateInput()}
                </div>
              ) : (
                <>
                  <IonLabel
                    className="wo-editable-input-label"
                    position="stacked"
                    color={required ? "danger" : undefined}
                  >
                    {labelText}
                  </IonLabel>
                  {calculateInput()}
                </>
              )}
              {required && (
                <IonNote
                  className="quick-wo-error-note"
                  hidden={!showError[meta.PropertyName]}
                  slot="error"
                  color="danger"
                  key={`${meta.PropertyName}-error`}
                >
                  {translatedMessages["errIsRequired"]?.MessageText.replace(
                    "@1",
                    meta.TranslatedIdText
                  )}
                </IonNote>
              )}
            </IonItem>
          );
        });

  const makeContent = () => {
    const standardFields = metadata.filter((field) => !isCustomField(field));
    const userDefinedFields = metadata.filter(isCustomField);

    return (
      <>
        {showSrField && (
          <div className="ion-padding sr-copy-field">
            <IonLabel className="sr-copy-label">
              {translations["lbl_copy_from_sr"] || "Copy from Standard Request"}
            </IonLabel>
            <IonItem
              class="wo-editable-container"
              button
              detail={false}
              onClick={() => setShowSrModal(true)}
            >
              <IonLabel color={srNumber ? undefined : "medium"}>
                {srNumber ||
                  translations["lbl_search_sr"] ||
                  "Search Standard Requests..."}
              </IonLabel>
              <IonIcon slot="end" icon={searchOutline} />
            </IonItem>
          </div>
        )}
        <IonList class="ion-padding">{renderFields(standardFields)}</IonList>
        {userDefinedFields.length > 0 && (
          <IonAccordionGroup className="wo-custom-fields ion-padding-horizontal">
            <IonAccordion value="user-defined-fields">
              <IonItem slot="header" color="light">
                <IonLabel>
                  {translations["lbl_grp_cust_info"] || "Custom Fields"}
                </IonLabel>
              </IonItem>
              <IonList slot="content" class="ion-padding-horizontal">
                {renderFields(userDefinedFields)}
              </IonList>
            </IonAccordion>
          </IonAccordionGroup>
        )}
        <ISSPopover
          popover={popover}
          popoverOpen={popoverOpen}
          setPopoverOpen={setPopoverOpen}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          setElementValue={
            propertyName === "NextReviewer"
              ? setNextReviewer
              : propertyName === "AssignedTo"
              ? setAssignedTo
              : propertyName === "Requestor"
              ? setRequestor
              : (value) => {
                  const match = propertyName.match(/^Technician(\d+)$/);
                  const index = match ? parseInt(match[1], 10) - 1 : -1;
                  const updatedTechnicians = [...technicians];
                  updatedTechnicians[index] = value;
                  setTechnicians(updatedTechnicians);
                }
          }
        />
      </>
    );
  };

  const title = isOffline
    ? translations["lbl_mob_offline_wo_creation"] || "Create WO In Offline Mode"
    : `${
        translations["lbl_category_equipment"] || "Equipment"
      } ${displayEquipId}`;

  return (
    <IonPage>
      <PageHeader
        title={title}
        subtitle={isOffline ? undefined : subtitle}
        showBack
      />
      <FilterModal
        isOpen={isFilterModalOpen}
        moduleName={
          filterModalModuleName?.toLowerCase() === "resource"
            ? "Resource"
            : filterModalModuleName
        }
        cancel={() => setIsFilterModalOpen(false)}
        confirm={onFilterModalConfirm}
      />
      {showSrField && (
        <StandardRequestModal
          isOpen={showSrModal}
          equipment={displayEquipId}
          onDismiss={() => setShowSrModal(false)}
          onSelect={onSrSelected}
        />
      )}
      <IonContent fullscreen>
        {metadataError && (
          <IonLabel color="danger">
            {translatedMessages["ErrRetrievingFields"]?.MessageText ||
              "Error retrieving the fields for the creation form"}
          </IonLabel>
        )}
        {templateError && (
          <IonLabel color="danger">
            Error retrieving the default values for the creation form
          </IonLabel>
        )}
        <IonLoading
          isOpen={showLoading}
          onDidDismiss={() => setShowLoading(false)}
          duration={5000}
        />
        {makeContent()}
        <div className="ion-padding">
          <input
            hidden
            type="file"
            id="filePickerWO"
            onChange={fileSelected}
            onClick={fileClicked}
          />
          <IonList>
            <IonListHeader key="attached-docs-list-header">
              <IonLabel>Attach Documents</IonLabel>
            </IonListHeader>
            {attachedDocuments.length === 0 ? (
              <IonItem lines="none" key="no-attached-documents">
                <IonLabel class="ion-text-wrap">
                  {translatedMessages["NoAttachedDocuments"]?.MessageText ||
                    "No Attached Documents. Use the upload button to add files and pictures."}
                </IonLabel>
              </IonItem>
            ) : (
              attachedDocuments.map((doc, index) => {
                return (
                  <IonItem lines="none" key={`${doc.FileName}-${index}`}>
                    <IonLabel>{doc.FileName}</IonLabel>
                    <IonButton slot="end" onClick={() => removeFile(index)}>
                      <IonIcon slot="icon-only" icon={trashOutline}></IonIcon>
                    </IonButton>
                  </IonItem>
                );
              })
            )}
          </IonList>
        </div>
        <IonFab slot="fixed" vertical="bottom" horizontal="center" edge>
          {isPlatform("ios") ? (
            <IonFabButton color="primary" onClick={() => selectFile()}>
              <IonIcon icon={cloudUpload}></IonIcon>
            </IonFabButton>
          ) : (
            <>
              <IonFabButton color="primary">
                <IonIcon icon={push}></IonIcon>
              </IonFabButton>
              <IonFabList side="top">
                <IonFabButton color="tertiary" onClick={() => takePhoto()}>
                  <IonIcon icon={camera}></IonIcon>
                </IonFabButton>
                <IonFabButton
                  color="tertiary"
                  onClick={() => {
                    selectFile();
                  }}
                >
                  <IonIcon icon={cloudUpload}></IonIcon>
                </IonFabButton>
              </IonFabList>
            </>
          )}
        </IonFab>
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              fill="solid"
              routerLink={routeBack}
              routerDirection="back"
            >
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            <IonButton
              fill="solid"
              disabled={enteriesInvalid()}
              onClick={() => submitWorkOrder()}
            >
              {translations["lbl_submit"] || "Submit"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default withAITracking(reactPlugin, CreateQuickWO, "CreateQuickWO");
